<template>
  <v-form
    ref="formRef"
    :key="sharedData.calculation_method"
    class="pump-form method-form"
    @submit.prevent=""
  >
    <!-- Basisdaten -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-pump</v-icon>
        <span>Basisdaten</span>
      </div>

      <!-- Amount -->
      <v-text-field
        v-model.number="form.amount"
        type="number"
        label="Anzahl"
        :error-messages="validationErrors?.amount"
        :hint="hint.amount"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.amount" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.amount }}</span>
          </v-tooltip>

          <!-- != null catches 0 as a valid reference value (falsy check would hide the button) -->
          <v-btn
            v-if="!reference && referenceData?.amount != null"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('amount')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <!-- Baujahr and Wartungszustand are deliberately absent. They only ever fed `eta_tot`, the
           fallback used when no duty point is known — and all three Pump2 modes compute the
           hydraulic energy explicitly from Q/H or the pump curve, so `eta_tot` is never consulted
           here. Verified by probing every mode against every value: nothing moved. They remain on
           Pump1, where all four modes do depend on them. -->
    </div>

    <!-- Pumpenkennlinie / Leistungswerte -->
    <div
      v-if="sharedData.calculation_method === 'performance_curve'"
      class="method-section"
    >
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-chart-bell-curve</v-icon>
        <span>Pumpenkennlinie</span>
      </div>

      <v-text-field
        v-model="form.powerFullLoad"
        inputmode="decimal"
        @blur="normaliseDecimal('powerFullLoad')"
        label="Leistung bei Volllast"
        suffix="kW"
        :error-messages="validationErrors?.power_full_load"
        class="mb-3"
      />

      <v-text-field
        v-model="form.powerPartLoad75"
        inputmode="decimal"
        @blur="normaliseDecimal('powerPartLoad75')"
        label="Leistung bei 75% Last"
        suffix="kW"
        :error-messages="validationErrors?.power_part_load_75"
        class="mb-3"
      />

      <v-text-field
        v-model="form.powerPartLoad50"
        inputmode="decimal"
        @blur="normaliseDecimal('powerPartLoad50')"
        label="Leistung bei 50% Last"
        suffix="kW"
        :error-messages="validationErrors?.power_part_load_50"
        class="mb-3"
      />

      <v-text-field
        v-model="form.powerPartLoad25"
        inputmode="decimal"
        @blur="normaliseDecimal('powerPartLoad25')"
        label="Leistung bei 25% Last"
        suffix="kW"
        :error-messages="validationErrors?.power_part_load_25"
        class="mb-3"
      />
    </div>

    <!-- Systemwirkungsgrad -->
    <div
      v-if="sharedData.calculation_method === 'system_efficiency'"
      class="method-section"
    >
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-percent-outline</v-icon>
        <span>Systemwirkungsgrad</span>
      </div>

      <v-text-field
        v-model="form.powerRating"
        inputmode="decimal"
        @blur="normaliseDecimal('powerRating')"
        label="Nennleistung"
        suffix="kW"
        :error-messages="validationErrors?.power_rating"
        class="mb-3"
      />

      <v-text-field
        v-model="form.flowRate"
        inputmode="decimal"
        @blur="normaliseDecimal('flowRate')"
        label="Volumenstrom"
        suffix="m³/h"
        :error-messages="validationErrors?.flow_rate"
        class="mb-3"
      />

      <v-text-field
        v-model="form.head"
        inputmode="decimal"
        @blur="normaliseDecimal('head')"
        label="Förderhöhe"
        suffix="m"
        :error-messages="validationErrors?.head"
        class="mb-3"
      />

      <v-text-field
        v-model.number="form.nominalWidthMm"
        label="Nenndurchmesser DN"
        suffix="mm"
        :error-messages="validationErrors?.nominal_width_mm"
        class="mb-3"
      />

      <!-- Datasheet BEP efficiency. Optional, but it replaces an estimate with a measurement, so
           it is surfaced rather than left looking like just another optional field (review N-9):
           without it the calculation falls back to the EU 547/2012 floor plus a margin. -->
      <v-text-field
        v-model="form.bepEfficiency"
        inputmode="decimal"
        @blur="normaliseDecimal('bepEfficiency')"
        label="Wirkungsgrad im Bestpunkt (BEP)"
        placeholder="z. B. 0,78"
        suffix="–"
        persistent-hint
        :error-messages="validationErrors?.bep_efficiency"
        :hint="bepEfficiencyHint"
        class="mb-3"
      >
        <template #prepend-inner>
          <v-icon :color="form.bepEfficiency ? 'success' : 'warning'" size="20">
            {{ form.bepEfficiency ? 'mdi-check-decagram' : 'mdi-alert-circle-outline' }}
          </v-icon>
        </template>
        <template #append>
          <v-tooltip location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>
              Pumpen-Wirkungsgrad im Bestpunkt laut Datenblatt.
              Einheitenlos (–), <strong>Dezimalzahl zwischen 0,05 und 0,95 — kein Prozentwert</strong>
              (also 0,78 und nicht 78). Komma oder Punkt sind beide möglich.<br>
              Anhaltswerte: kleine Pumpen (&lt; 5 kW) 0,50–0,70 · mittlere (5–50 kW) 0,65–0,80 ·
              große (&gt; 50 kW) 0,75–0,88.<br>
              Größer ist besser — hier also umgekehrt zum EEI.
            </span>
          </v-tooltip>
        </template>
      </v-text-field>

      <!-- All four are required by `system_efficiency`, so each must be able to show its own
           error. Without the binding the message only reached the Action-level error object and
           the field merely looked empty — which is how a `regulation` code carried in from
           another method stayed invisible. -->
      <v-autocomplete
        v-model="form.gear"
        :items="gears"
        item-title="text"
        item-value="value"
        label="Getriebeart"
        :error-messages="validationErrors?.gear"
        class="mb-3"
      />

      <v-autocomplete
        v-model="form.regulation"
        :items="regulations"
        item-title="text"
        item-value="value"
        label="Regelung"
        :error-messages="validationErrors?.regulation"
        class="mb-3"
      />

      <v-autocomplete
        v-model="form.efficiencyClass"
        :items="efficiencyClasses"
        item-title="text"
        item-value="value"
        label="Effizienzklasse"
        :error-messages="validationErrors?.efficiency_class"
        class="mb-3"
      />

      <v-autocomplete
        v-model="form.poles"
        :items="poleList"
        label="Polzahl"
        :error-messages="validationErrors?.poles"
        class="mb-3"
      />
    </div>

    <!-- Nassläufer EEI (EU 641/2009) -->
    <div
      v-if="sharedData.calculation_method === 'eei'"
      class="method-section"
    >
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-water-percent</v-icon>
        <span>Nassläufer (EEI)</span>
      </div>

      <!-- EEI is dimensionless and small (0…1,5). Users kept guessing at the expected magnitude,
           so the typical values are stated next to the field rather than hidden in a tooltip. -->
      <v-text-field
        v-model="form.eei"
        inputmode="decimal"
        label="Energieeffizienzindex (EEI)"
        placeholder="z. B. 0,20"
        suffix="–"
        persistent-hint
        :hint="eeiHint"
        :error-messages="validationErrors?.eei"
        class="mb-3"
        @blur="normaliseDecimal('eei')"
      >
        <template #append>
          <v-tooltip location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>
              Energieeffizienzindex nach EU 641/2009, Datenblattwert der Nassläuferpumpe.
              Einheitenlos (–), Dezimalzahl zwischen 0 und 1,5, kein Prozentwert.
              Komma oder Punkt sind beide möglich.<br>
              Referenzwert der besten Technik: 0,20 · Grenzwert Neupumpe seit 2015: ≤ 0,23 ·
              alte ungeregelte Pumpe: ca. 0,8–1,0.<br>
              Kleiner ist besser — die Bestandspumpe hat also den größeren Wert.
            </span>
          </v-tooltip>
        </template>
      </v-text-field>

      <v-text-field
        v-model="form.flowRate"
        inputmode="decimal"
        @blur="normaliseDecimal('flowRate')"
        label="Volumenstrom"
        suffix="m³/h"
        :error-messages="validationErrors?.flow_rate"
        class="mb-3"
      />

      <v-text-field
        v-model="form.head"
        inputmode="decimal"
        @blur="normaliseDecimal('head')"
        label="Förderhöhe"
        suffix="m"
        :error-messages="validationErrors?.head"
        class="mb-3"
      />

      <div class="shared-form-hint text-body-2 text-medium-emphasis">
        EEI-Methode gilt für kleine Nassläufer (1 W ≤ hydraulische Leistung ≤ 2500 W). Das
        Ergebnis entspricht dem standardisierten Lastprofil, skaliert auf die Betriebsstunden.
      </div>
    </div>

    <!-- Lastverteilung (nur für Kennlinien-/Systemwirkungsgrad-Methoden) -->
    <div
      v-if="sharedData.calculation_method !== 'eei'"
      class="method-section"
    >
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-gauge</v-icon>
        <span>Lastverteilung</span>
      </div>

      <v-text-field
        v-model.number="form.fullLoad"
        label="Anteil Volllast"
        suffix="%"
        :error-messages="validationErrors?.full_load"
        class="mb-3"
      />

      <v-text-field
        v-model.number="form.partLoad75"
        label="Anteil 75% Last"
        suffix="%"
        :error-messages="validationErrors?.part_load_75"
        class="mb-3"
      />

      <v-text-field
        v-model.number="form.partLoad50"
        label="Anteil 50% Last"
        suffix="%"
        :error-messages="validationErrors?.part_load_50"
        class="mb-3"
      />

      <v-text-field
        v-model.number="form.partLoad25"
        label="Anteil 25% Teillast"
        suffix="%"
        :error-messages="validationErrors?.part_load_25"
        class="mb-3"
      />
    </div>

    <!-- Betriebsstunden -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-clock-outline</v-icon>
        <span>Betriebsstunden</span>
      </div>

      <!-- Norm uptime toggle -->
      <v-checkbox
        v-model="form.checkNormUptime"
        label="Norm-Betriebsstunden"
        :error-messages="validationErrors?.check_norm_uptime"
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.check_norm_uptime" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.check_norm_uptime }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.check_norm_uptime != null"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('check_norm_uptime')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-checkbox>

      <!--
        Norm uptime select.
        NOTE: normUptimeObject is intentionally NOT in the fields map.
        It is a local-only UI binding used to let the user pick a norm uptime entry.
        On selection, we extract the id (-> form.normUptimeId for the backend)
        and the hours value (-> form.uptime for display/calculation).
        Only normUptimeId travels to the backend via the fields map.
      -->
      <v-autocomplete
        v-if="form.checkNormUptime"
        v-model="normUptimeObject"
        :items="uptimes"
        item-title="name"
        item-value="id"
        return-object
        label="Norm-Betriebsstunden"
        :error-messages="validationErrors?.norm_uptime"
        class="mb-3 ml-8"
      >
        <template #append>
          <v-tooltip v-if="hint.norm_uptime" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.norm_uptime }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.norm_uptime != null"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('norm_uptime')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <!-- Manual uptime (disabled when norm uptime is active) -->
      <v-text-field
        v-model.number="form.uptime"
        label="Betriebsstunden"
        suffix="h/a"
        :disabled="form.checkNormUptime"
        :error-messages="validationErrors?.uptime"
        :hint="hint.uptime"
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.uptime" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.uptime }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.uptime != null"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('uptime')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <!--
        Integrated pump management — DIN V 18599-5 §6.3.2.2, Gl. (56).
        A RUNTIME factor, so it belongs here and applies to every calculation method.
      -->
      <v-autocomplete
        v-model="form.pumpManagement"
        :items="pumpManagements"
        item-title="text"
        item-value="value"
        label="Pumpenmanagement"
        :error-messages="validationErrors?.pump_management"
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.pump_management" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.pump_management }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.pump_management != null"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('pump_management')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <!--
        Hydraulic balancing — DIN V 18599-7 D.2.6.
        Shown ONLY for system_efficiency: that is the one method which derives electrical power
        from the hydraulics, so it is the only place the correction is not already contained in
        the input. For measured or declared power the over-pumping is in the reading already, and
        applying it again would double-count (finding S-9).
      -->
      <v-autocomplete
        v-if="sharedData.calculation_method === 'system_efficiency'"
        v-model="form.hydraulicBalance"
        :items="hydraulicBalances"
        item-title="text"
        item-value="value"
        label="Hydraulischer Abgleich"
        :error-messages="validationErrors?.hydraulic_balance"
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.hydraulic_balance" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.hydraulic_balance }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.hydraulic_balance != null"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('hydraulic_balance')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>
    </div>
  </v-form>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import api from '@/plugins/axios'
import { useBaseForm } from '@/composables/useBaseForm'
import { djangoChoicesToVuetify } from '@/utils'

// ============================================================
// Props & Emits
// ============================================================

const props = defineProps({
  modelValue:       Object,
  reference:        Boolean,
  planned:          Boolean,
  otherData:        Object,
  sharedData:       Object,
  validationErrors: Object,
  methodValue:      String,
})

const emit = defineEmits(['update:modelValue'])

// ============================================================
// Field mapping (frontend key -> backend key)
//
// normUptimeObject is intentionally excluded — it is a local UI
// binding only. The backend receives the selected id via normUptimeId.
// Fields that only appear under one calculation_method (e.g. powerFullLoad,
// powerRating) are still included here — useBaseForm skips null/undefined
// fields in data.get(), so inactive section fields simply won't be sent.
// ============================================================

const fields = {
  amount:          'amount',
  flowRate:        'flow_rate',
  powerRating:     'power_rating',
  head:            'head',
  nominalWidthMm:  'nominal_width_mm',
  bepEfficiency:   'bep_efficiency',
  eei:             'eei',
  gear:            'gear',
  regulation:      'regulation',
  efficiencyClass: 'efficiency_class',
  poles:           'poles',
  fullLoad:        'full_load',
  partLoad75:      'part_load_75',
  partLoad50:      'part_load_50',
  partLoad25:      'part_load_25',
  powerFullLoad:   'power_full_load',
  powerPartLoad75: 'power_part_load_75',
  powerPartLoad50: 'power_part_load_50',
  powerPartLoad25: 'power_part_load_25',
  checkNormUptime: 'check_norm_uptime',
  uptime:          'uptime',
  normUptimeId:    'norm_uptime',     // sends only the id, not the full object
  pumpManagement:  'pump_management',   // f_d,PM  — DIN V 18599-5 §6.3.2.2 (runtime factor)
  hydraulicBalance: 'hydraulic_balance', // f_Abgl — DIN V 18599-7 D.2.6 (derived modes only)
}

// ============================================================
// Base form composable
// ============================================================

const {
  form,
  hint,
  referenceData,
  copyFromReference,
  fetchHints,
} = useBaseForm({
  value:       computed(() => props.modelValue),
  fields,
  shared:      computed(() => false),           // always a normal (non-shared) child form
  reference:   computed(() => props.reference),
  planned:     computed(() => props.planned),
  otherData:   computed(() => props.otherData),
  sharedData:  computed(() => props.sharedData),
  methodValue: computed(() => props.methodValue),
  emit:        (e, p) => emit(e, p),
})

// ============================================================
// Local state
// ============================================================

const uptimes          = ref([])
const efficiencyClasses = ref([])
const pumpManagements  = ref([])
const hydraulicBalances = ref([])

/**
 * Decimal inputs.
 *
 * These fields used `v-model.number` on a plain text field, which makes a decimal impossible to
 * type: the modifier coerces the intermediate "0." to 0, Vuetify re-renders the field from the
 * model as "0", and the separator is eaten on every keystroke. Entering 0.8 or 0.15 for the EEI
 * simply could not be done.
 *
 * So they keep the raw string while the user types and are normalised on blur. That also lets a
 * German keyboard use the decimal comma, which `.number` turned into NaN.
 */
const normaliseDecimal = (key) => {
  const raw = form[key]
  if (raw === '' || raw === null || raw === undefined) {
    form[key] = null
    return
  }
  const parsed = Number(String(raw).replace(',', '.'))
  form[key] = Number.isFinite(parsed) ? parsed : null
}

// EEI hint. Reference and planned need very different magnitudes — an old uncontrolled circulator
// sits near 1.0 while a new one must be ≤ 0.23 — so the hint follows the variant being edited.
const eeiHint = computed(() =>
  props.reference
    ? 'Bestandspumpe: ungeregelt meist 0,8–1,0; elektrisch geregelt ca. 0,5–0,7.'
    : 'Neupumpe: Grenzwert ≤ 0,23, Referenzwert bester Technik 0,20. Kleiner ist besser.',
)

// Tells the user which of the two paths their input is currently on (review N-9). Without a
// datasheet BEP the hydraulic efficiency is estimated from the EU 547/2012 minimum for the entered
// duty point — defensible, but an estimate, and the single largest assumption in this mode.
const bepEfficiencyHint = computed(() =>
  form.bepEfficiency
    ? 'Datenblattwert wird verwendet.'
    : 'Dezimalzahl 0,05–0,95 (kein Prozentwert). Ohne Datenblattwert wird der Wirkungsgrad aus '
      + 'dem EU-547/2012-Mindestwirkungsgrad für diesen Betriebspunkt geschätzt — für ein '
      + 'belastbares Ergebnis bitte den BEP-Wirkungsgrad aus dem Datenblatt eintragen.',
)


// normUptimeObject is local-only — holds the full object from the autocomplete
// so we can extract both the id (for the backend) and the hours value (for uptime).
// It is NOT in the fields map and does NOT travel to the backend directly.
const normUptimeObject = ref(null)

// Static choice lists — fixed values, no API call needed
const poleList = [2, 4, 6]

const gears = [
  { value: 1, text: 'Direktantrieb' },
  { value: 2, text: 'Flachriemen' },
  { value: 3, text: 'Keilriemen' },
]

const regulations = [
  { value: 1, text: 'ungeregelt' },
  { value: 2, text: 'zweistufig' },
  { value: 3, text: 'Konstantdruckregelung' },
  { value: 4, text: 'Proportionaldruckregelung' },
]

// ============================================================
// Watchers
// ============================================================

// When the user selects a norm uptime entry, extract:
//   - its id into form.normUptimeId  (this is what gets sent to the backend)
//   - its hours value into form.uptime (so the uptime field reflects the norm value)
// Guard: only apply when checkNormUptime is active, to avoid overwriting
// a manually entered uptime if the user deselects norm uptime mode.
watch(normUptimeObject, obj => {
  if (form.checkNormUptime && obj) {
    form.normUptimeId = obj.id
    form.uptime       = obj.norm_uptime
  }
})

// ============================================================
// API calls
// ============================================================

async function fetchNormUptimes() {
  const { data } = await api.get('/methods/pump2/norm_uptimes/')
  uptimes.value = data
}

async function fetchEfficiencyClasses() {
  const { data } = await api.get('/methods/pump2/efficiency-classes/')
  efficiencyClasses.value = djangoChoicesToVuetify(data)
}

async function fetchPumpManagements() {
  const { data } = await api.get('/methods/pump2/pump-management/')
  pumpManagements.value = djangoChoicesToVuetify(data)
}

async function fetchHydraulicBalances() {
  const { data } = await api.get('/methods/pump2/hydraulic-balance/')
  hydraulicBalances.value = djangoChoicesToVuetify(data)
}

// ============================================================
// Expose (for parent form validation)
// ============================================================

const formRef      = ref(null)
const validateForm = () => formRef.value?.validate?.()

defineExpose({
  validateForm,
  fields: Object.values(fields),
})

// ============================================================
// Lifecycle
// ============================================================

onMounted(async () => {
  await fetchNormUptimes()
  await fetchEfficiencyClasses()
  await fetchPumpManagements()
  await fetchHydraulicBalances()
  await fetchHints()
})
</script>

<style scoped lang="scss">
.pump-form {
  width: 100%;
}

/* Field styling */
.pump-form :deep(.v-field) {
  border-radius: 18px;
}

.pump-form :deep(.v-input) {
  margin-bottom: 0.35rem;
}

/* Section wrapper */
.method-section {
  margin-bottom: 1.25rem;
  padding: 1rem;
  border-radius: 24px;
  background-color: rgba(var(--v-theme-primary), 0.025);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
}

/* Section heading */
.method-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;
  border-radius: 18px;
  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);
  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  font-size: 1rem;
  font-weight: 700;
}

/* Checkboxes */
.pump-form :deep(.v-checkbox) {
  padding: 0.35rem 0.65rem;
  border-radius: 18px;
  background-color: rgba(var(--v-theme-primary), 0.025);
}

/* Nested/dependent fields */
.pump-form :deep(.ml-8) {
  margin-left: 1.5rem !important;
  padding-left: 1rem;
  border-left: 2px solid rgba(var(--v-theme-primary), 0.12);
}

/* Hint/copy icons (fixed typos: .v-input__appen* and .v-field__appe*d-inner) */
.pump-form :deep(.v-input__append),
.pump-form :deep(.v-field__append-inner) {
  align-items: center;
}

.pump-form :deep(.v-input__append .v-icon),
.pump-form :deep(.v-field__append-inner .v-icon) {
  color: rgb(var(--v-theme-primary));
  opacity: 0.85;
}

.pump-form :deep(.v-input__append .v-btn),
.pump-form :deep(.v-field__append-inner .v-btn) {
  border-radius: 999px;
  color: rgb(var(--v-theme-primary));
}

.pump-form :deep(.v-input__append .v-btn:hover),
.pump-form :deep(.v-field__append-inner .v-btn:hover) {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* Disabled fields */
.pump-form :deep(.v-field--disabled) {
  opacity: 0.72;
}

@media (max-width: 700px) {
  .method-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .method-section-heading {
    align-items: flex-start;
    font-size: 0.95rem;
  }

  .pump-form :deep(.ml-8) {
    margin-left: 0.75rem !important;
    padding-left: 0.75rem;
  }
}
</style>